﻿import { LocalizationService } from "../../localization/localization";

export class AlertBinding {
  /** The type of alert to show. */
  public alertType = "@";
  /** The header to show in the alert. */
  public alertHeader = "@";
  /** The message to show in the alert. */
  public alertMessage = "@";
  /** The actions to show inline in the alert. See ServiceDesk.Controls.Alert.AlertAction. */
  public alertActions = "<";
  /** fn to execute on alert close. Can be used to reset the boolean to toggle the variable used to hide/show the alert. */
  public alertOnclose = "&?";
  /** If true, will remove the close button. */
  public hideCloseButton = "@?";
  /** If true, will hide the control. */
  public dismissed = "=?";
  /** Will dismiss the alert after X milliseconds. This is a one time binding. */
  public dismissAfterMs = "@?";
  /** Will show a countdown timer for dismissal. */
  public showTimer = "@?";
  /** Custom glyph which will override the default one which is based on the alertType */
  public customGlyph = "@?";
  /** Will force the alert to show as two lines regardless of having both header and message. */
  public forceMultiLine = "@?";
  /** Will test integrate sdk module. */
  public alertDetails = "@?";
}

export type AlertType = "Error" | "Information" | "Warning" | "Success";

export interface AlertAction {
  linkText: string;
  url?: string;
  callback?: (closeAlert?: () => void) => any;
  biId?: string;
}

export class Alert {
  public alertActions: Array<AlertAction> | {};
  public alertClass: string;
  public alertHeader: string;
  public alertMessage: string;
  public alertType: AlertType;
  public glyphClass: string;
  public glyphTypeAriaLabel: string;
  public biArea: string;
  public dismissed: boolean;
  public dismissAfterMs: string;
  public alertOnclose: () => void;
  public showTimer: boolean;
  public timeRemaining: number;
  private timerStarted = false;
  public customGlyph: string;
  public forceMultiLine: boolean;
  public shownMessage: string;
  public shownHeader: string;
  public boundCloseMethod = this.alertClosed.bind(this);
  public isExpanded = false;
  public alertDetails: string;
  private buttonLabel: string;
  public showDetailsText: string;
  public hideDetailsText: string;
  // @ngInject
  constructor(
    private $element: ng.IAugmentedJQuery,
    private sdLocalizationService: LocalizationService,
    private $timeout: ng.ITimeoutService,
    private $scope: ng.IScope,
    private $interval: ng.IIntervalService
  ) {
    this.showDetailsText = sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_CONTENT_TOGGLE_SHOW_DETAILS_"
    );
    this.hideDetailsText = sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_CONTENT_TOGGLE_HIDE_DETAILS_"
    );
    this.buttonLabel = this.showDetailsText;
  }

  // component event methods:
  public $onInit(): void {
    this.refreshView();
  }
  public $onChanges(changesObj: any): void {
    this.refreshView();
    if (changesObj.dismissAfterMs) {
      this.manageTimer();
    }
  }

  public toggle(): void {
    this.setCollapsed(!this.isExpanded);
  }

  private setCollapsed(expanded: boolean) {
    this.buttonLabel = this.isExpanded ? this.showDetailsText : this.hideDetailsText;
    this.isExpanded = expanded;
  }

  public alertClosed(): void {
    this.dismissed = true;
    if (this.alertOnclose) {
      this.alertOnclose();
    }
  }

  private manageTimer(): void {
    if (this.dismissAfterMs && !this.timerStarted) {
      this.timerStarted = true;
      let dismissTime = parseInt(this.dismissAfterMs);
      let timeStart = new Date().getTime();

      this.$scope.$evalAsync(() => {
        this.timeRemaining = this.calculateTimeRemaining(timeStart, dismissTime);
      });

      let intervalPromise = this.$interval(
        () => {
          this.timeRemaining = this.calculateTimeRemaining(timeStart, dismissTime);
          if (this.timeRemaining <= 0) {
            this.dismissed = true;
            this.$interval.cancel(intervalPromise);
          }
        },
        100,
        undefined,
        true
      );
      this.$scope.$on("$destroy", () => {
        this.$interval.cancel(intervalPromise);
      });
    }
  }

  private getSecondsFromMs(time: number) {
    return Math.ceil(time / 1000);
  }

  private calculateTimeRemaining(startTime: number, totalTime: number) {
    let currentTime = new Date().getTime();
    return this.getSecondsFromMs(totalTime - (currentTime - startTime));
  }

  private refreshView(): void {
    if (!this.alertMessage && !this.forceMultiLine) {
      this.shownMessage = this.alertHeader;
      this.shownHeader = undefined;
    } else if (this.alertMessage && !this.alertHeader && this.forceMultiLine) {
      this.shownHeader = this.alertMessage;
      this.shownMessage = undefined;
    } else {
      this.shownHeader = this.alertHeader;
      this.shownMessage = this.alertMessage;
    }

    if (angular.isUndefined(this.alertType)) {
      this.alertType = "Information";
    }

    // set class and other props values based on alert type:
    switch (this.alertType.toLowerCase()) {
      case "information":
        this.alertClass = "f-information";
        this.glyphClass = "sd-glyph-information";
        this.glyphTypeAriaLabel = this.sdLocalizationService.getLocalizedString(
          "ShellCommon",
          "_CONTROLS_ALERT_INFORMATION_MESSAGE_"
        );
        break;
      case "warning":
        this.alertClass = "f-warning";
        this.glyphClass = "sd-glyph-warning";
        this.glyphTypeAriaLabel = this.sdLocalizationService.getLocalizedString(
          "ShellCommon",
          "_CONTROLS_ALERT_WARNING_MESSAGE_"
        );
        break;
      case "error":
        this.alertClass = "f-error";
        this.glyphClass = "sd-glyph-error";
        this.glyphTypeAriaLabel = this.sdLocalizationService.getLocalizedString(
          "ShellCommon",
          "_CONTROLS_ALERT_ERROR_MESSAGE_"
        );
        break;
      case "success":
        this.alertClass = "f-success";
        this.glyphClass = "sd-glyph-success";
        this.glyphTypeAriaLabel = this.sdLocalizationService.getLocalizedString(
          "ShellCommon",
          "_CONTROLS_ALERT_SUCCESS_MESSAGE_"
        );
        break;
      default:
        throw new Error(`Invalid alert type specified: ${this.alertType}`);
    }
    if (this.customGlyph) {
      this.glyphClass = this.customGlyph;
    }
  }
}
export const sdAlert: ng.IComponentOptions = {
  controller: Alert,
  templateUrl: "../ShellCommon/Controls/alert/alert.html",
  bindings: <any>new AlertBinding()
};
